"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useI18n } from "~/locales/client";
import { addCommaToNumber } from "~/utils/generalUtils";
import { getFilesFromIdb } from "~/utils/indexedDbFileStore";
import { colors, spacing } from "~/utils/tailwindUtils";
import Flex from "../flex/Flex";
import { Icon, IconName } from "../icons/Icon";
import Text from "../texts/Text";
import Badge from "../badges/Badge";

export type FileUploadProps = {
  onChange?: (value: File[], fileType?: string) => void;
  onRemove?: (index: number) => void;
  limit?: number;
  isDisabled?: boolean;
  multiple?: boolean;
  acceptFileTypes?: string;
  labelClassName?: string;
  uploadedFilesLabel?: string;
  fileTypeOptions?: string[]; // 👈 e.g. ["Invoice", "Contract", "Photo"]
  initialFiles?: File[];
  initialFileIds?: string[];
  displayUploadedFiles?: boolean;
  getFileType?: (file: File, index: number) => string | undefined; // Function to get file type for badge display
};
// default file size limit in MB
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const DEFAULT_FILE_TYPES = ".pdf,.jpeg,.jpg,.png,.xls,.xlsx,.doc,.docx,.zip";

/**
 * FileUpload component that allows the user to upload files to the server
 * @param onChange - function that is called when the user uploads a file
 * @param limit - the maximum number of files that can be uploaded by MB
 * @returns FileUpload component
 */
const FileUpload = ({
  onChange,
  onRemove,
  limit = MAX_FILE_SIZE_BYTES,
  isDisabled,
  multiple,
  acceptFileTypes = DEFAULT_FILE_TYPES,
  labelClassName,
  uploadedFilesLabel,
  fileTypeOptions,
  initialFiles,
  initialFileIds,
  displayUploadedFiles = true,
  getFileType,
}: FileUploadProps) => {
  const translate = useI18n();
  const [files, setFiles] = useState<File[]>([]);
  const [selectedFileType, setSelectedFileType] = useState<string>(""); // 👈 NEW
  const initializedRef = useRef(false);

  // Initialize files from props or IndexedDB on mount/prop change
  useEffect(() => {
    async function init() {
      if (initialFiles?.length) {
        setFiles(initialFiles);
        initializedRef.current = true;
        return;
      }
      if (initialFileIds?.length) {
        const loaded = await getFilesFromIdb(initialFileIds);
        if (loaded.length > 0) setFiles(loaded);
        initializedRef.current = true;
      }
    }
    // Reset initialization when initialFiles or initialFileIds change
    if (initialFiles || initialFileIds) {
      initializedRef.current = false;
      init();
    }
  }, [initialFiles, initialFileIds]);

  const handleFiles = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = event.target.files ? Array.from(event.target.files) : [];

      // Filter out files that exceed the size limit
      const validFiles = selectedFiles.filter((file) => file.size <= limit);

      if (validFiles.length === 0) return;
      if (fileTypeOptions && !selectedFileType) {
        alert("Please select a file type before uploading.");
        event.target.value = "";
        return;
      }

      const newFiles = multiple ? [...files, ...validFiles] : [validFiles[0]];
      setFiles(newFiles);
      onChange?.(newFiles, selectedFileType);

      // ✅ Reset file type after successful upload
      setSelectedFileType("");
      event.target.value = ""; // reset input too, so same file can be re-uploaded
    },
    [files, limit, multiple, onChange, fileTypeOptions, selectedFileType]
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLLabelElement>) => {
      event.preventDefault();
      if (fileTypeOptions && !selectedFileType) {
        alert("Please select a file type before uploading.");
        return;
      }

      const droppedFiles = Array.from(event.dataTransfer.files).filter((file) => file.size <= limit);

      if (droppedFiles.length === 0) return;

      const newFiles = multiple ? [...files, ...droppedFiles] : [droppedFiles[0]];
      setFiles(newFiles);
      onChange?.(newFiles, selectedFileType);

      // ✅ Reset file type after successful upload
      setSelectedFileType("");
    },
    [files, limit, multiple, onChange, fileTypeOptions, selectedFileType]
  );

  const removeItem = useCallback(
    (index: number) => {
      const newFiles = files.filter((_, i) => i !== index);
      setFiles(newFiles);
      onChange?.(newFiles, selectedFileType);
      onRemove?.(index);
    },
    [files, onChange, onRemove, selectedFileType]
  );

  return (
    <Flex className="w-full mx-auto flex-col gap-y-4">
      {fileTypeOptions && (
        <Flex className="flex-col gap-1 mt-2">
          <Text fontWeight="medium" color="lightGray" className="text-[8px] md:text-[10px]">
            {translate("general.select-file-type")}
          </Text>
          <select
            className={twMerge(
              "border border-gray-300 rounded-md p-2 text-sm cursor-pointer focus:outline-none focus:ring-0 focus:border-gray-400",
              isDisabled && "opacity-50 cursor-not-allowed"
            )}
            value={selectedFileType}
            onChange={(e) => setSelectedFileType(e.target.value)}
            disabled={isDisabled}
          >
            <option value="">{translate("general.select")}</option>
            {fileTypeOptions.map((type) => (
              <option key={type} value={type} className="cursor-pointer">
                {type}
              </option>
            ))}
          </select>
        </Flex>
      )}

      <label
        data-testid="file-upload-label"
        className={twMerge(
          "flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100",
          isDisabled && "opacity-50 hover:bg-gray-50 cursor-not-allowed",
          labelClassName
        )}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        <input
          data-testid="file-upload-input"
          type="file"
          multiple={multiple}
          className="hidden"
          onChange={handleFiles}
          accept={acceptFileTypes}
          disabled={isDisabled}
        />
        <Flex className="flex-col items-center gap-4 p-4">
          <Icon name={IconName.Upload} color={colors.black} size={spacing[5]} customStrokeWidth={2} />
          <Text color="black" fontWeight="semibold" className="text-[8px] text-center md:text-xs">
            <div
              dangerouslySetInnerHTML={{
                __html: translate("fileUploader.title"),
              }}
            />
          </Text>
          <Text color="lightGray" className="text-[8px] md:text-[8px] text-center">
            {translate("fileUploader.description")}
          </Text>
        </Flex>
      </label>

      {files.length > 0 && displayUploadedFiles && (
        <ul className="mt-4 space-y-2">
          <Text fontWeight="medium" className="text-gray-500 text-[8px] md:text-xs">
            {uploadedFilesLabel || translate("general.uploaded-files")}
          </Text>
          {files.map((file, index) => {
            const fileType = getFileType?.(file, index);
            return (
              <Flex
                key={`${file.name}-${file.lastModified}-${index}`}
                className="gap-2 py-1 border-b border-gray-100 justify-between items-center"
              >
                <Flex className="gap-x-4 items-center">
                  <Icon name={IconName.Document} size={spacing[4]} />
                  <li className="text-sm font-medium truncate max-w-60 text-wrap">{file.name}</li>
                  {fileType && (fileType === "Invoice" || fileType === "Flashlist") && (
                    <Badge className="bg-gray-100 text-gray-700">{fileType}</Badge>
                  )}
                </Flex>
                <Flex className="gap-x-4">
                  <Flex className="px-2 py-1 bg-gray-100 rounded-full">
                    <Text color="lightGray" className="text-xs min-w-max">
                      {addCommaToNumber(file.size / 1000)} KB
                    </Text>
                  </Flex>
                  <Icon
                    name={IconName.Trash}
                    size={spacing[4]}
                    onClick={() => removeItem(index)}
                    className="cursor-pointer"
                  />
                </Flex>
              </Flex>
            );
          })}
        </ul>
      )}
    </Flex>
  );
};

export default FileUpload;
